<template>
	<div :class="$style['app-checkbox-wrapper']">
		<div
			:class="[$style['app-checkbox'], {
				[$style['app-checkbox--label']]: label
			}]"
		>
			<input
				:id="`checkbox-${_uid}`"
				v-bind="$attrs"
				:checked="internalChecked"
				:value="value"
				:disabled="!!disabled"
				type="checkbox"
				@change="change"
			/>
			<label :for="`checkbox-${_uid}`">
				<slot>
					{{ label }}
				</slot>
				<span
					v-if="required"
					:class="$style['app-checkbox__required']"
				>*</span>
			</label>
			<div
				:class="[$style['app-checkbox__mark'], {
					[$style['app-checkbox__mark--indeterminate']]: indeterminate && !internalChecked
				}]"
			>
				<FontAwesomeIcon
					v-if="indeterminate && !internalChecked"
					:icon="['fas', 'minus']"
				/>
				<FontAwesomeIcon
					v-else-if="internalChecked"
					:icon="['far', 'check']"
				/>
			</div>
		</div>
		<div
			v-if="actions.length && !disabled"
			:class="$style['checkbox-dropdown']"
			@click.stop="dropdownToggle"
		>
			<FontAwesomeIcon
				:icon="['fas', 'caret-down']"
			/>
		</div>
		<DropdownMenu
			ref="dropdown"
			:class="$style['dropdown-menu']"
			auto-position
		>
			<DropdownMenuList
				:list="actions"
				@dropdown-toggle="dropdownToggle($event)"
			/>
		</DropdownMenu>
	</div>
</template>
<script>
import DropdownMenu from '@c:essentials/DropdownMenu'
import DropdownMenuList from '@c:essentials/DropdownMenu/DropdownMenuList'
export default {
	name: 'BaseCheckbox',
	components: {
		DropdownMenu,
		DropdownMenuList
	},
	model: {
		prop: 'value',
		event: 'change'
	},
	props: {
		disabled: {
			type: [Boolean, Number, String],
			default: false
		},
		actions: {
			type: Array,
			default: () => []
		},
		label: {
			type: String,
			default: null
		},
		checked: {
			type: [String, Boolean],
			default: false
		},
		value: {
			type: [Boolean, String],
			default: null
		},
		indeterminate: {
			type: Boolean,
			default: false
		},
		required: {
			type: Boolean,
			default: false
		}
	},
	data () {
		const propsData = this.$options.propsData || {}
		const hasCheckedProp = Object.prototype.hasOwnProperty.call(propsData, 'checked')

		return {
			hasCheckedProp,
			internalChecked: hasCheckedProp ? this.checked : this.value
		}
	},
	watch: {
		checked: {
			handler (val) {
				if (this.hasCheckedProp) {
					this.internalChecked = val
				}
			}
		},
		value: {
			handler (val) {
				if (!this.hasCheckedProp) {
					this.internalChecked = val
				}
			}
		}
	},
	methods: {
		dropdownToggle (_e) {
			this.$refs['dropdown'].toggle()
		},
		change (e) {
			this.internalChecked = e.target.checked
			this.$emit('change', e.target.checked)
		}
	}
}
</script>

<style lang="scss" module>
	.app-checkbox-wrapper {
		display: flex;
		align-items: center;
		position: relative;
	}

	.dropdown-menu {
		top: 20px;
		transform-origin: top;
	}

	.checkbox-dropdown {
		user-select: none;
		height: 18px;
		min-width: 18px;
		margin-left: 1px;
		margin-bottom: -1px;
		display: flex;
		justify-content: center;
		align-items: center;

		&:hover{
			cursor: pointer;
			color: getColor(text) !important;
			// background-color: #bbb; //TODO add hover effects
		}
	}

	.app-checkbox {
		position: relative;
		height: 20px;

		input {
			opacity: 0;
			position: absolute;
			margin: 0;
		}

		&__mark {
			pointer-events: none;
			position: absolute;
			left: 0;
			top: 2px;
			width: 18px;
			height: 18px;
			display: flex;
			align-items: center;
			justify-content: center;
			opacity: 0;
			font-size: 12px;
			transform: rotate(-90deg) scale(0);
			transition: opacity 0.1s linear, transform 0.25s ease;

			&--indeterminate {
				opacity: 1;
				transform: none;
				transition: none;
				color: getColor(tertiary);
			}
		}

		label {
			display: flex;
			position: relative;
			font-size: 14px;
			margin: 0;
			padding-left: 18px;
			line-height: 1.5;
			cursor: pointer;

			&::before {
				content: '';
				position: absolute;
				left: 0;
				top: 2px;
				width: 18px;
				height: 18px;
				border: 1px solid getColor(border-darker);
				border-radius: 2px;
				background-color: getColor(input-background);
				transition: 0.2s ease;
			}

			&:hover {
				&::before {
					border-color: getColor(tertiary);
				}
			}
		}

		input:focus + label::before {
			border-color: getColor(tertiary);
		}

		input:checked + label + div {
			opacity: 1;
			transform: rotate(0deg) scale(1);
			color: getColor(white);
		}

		input:checked + label::before {
			background-color: getColor(primary);
			border-color: getColor(primary);
		}

		input:disabled:checked + label + div {
			color: getColor(disabled-text);
		}

		&--label {
			label {
				padding-left: 25.5px;
			}
		}

		input:disabled + label {
			pointer-events: none;
			color: getColor(disabled-text);

			&::before {
				border-color: getColor(border-minor) !important;
				background-color: getColor(input-background-disabled);
			}
		}

		&__required {
			color: getColor(danger);
			margin-left: 2px;
		}
	}
</style>
